import { h } from 'preact'
import Axios, {AxiosResponse} from 'axios'
import Homepage, {
  HomepageElement,
  HomepageElementInterface,
  CategoryAndContentStreamHomepageElement,
  KnowledgeBaseHomepageElement,
  HeroBannerHomepageElement,
  GenericHomepageElementTypes,
  FeaturedTopicsElement
} from './index'
import {QuicklinkType} from '../quick-links/QuickLinksManager'
import {Tab} from '../homepage-tabs/TabsConfigurator'
import StateTracker, {StateTrackerState} from '../state-tracker'
import tracker from '../../components/tracker/publish'
import DynamicSaveBar from '../ui-components/DynamicSaveBar'
import {arrayMove} from 'react-sortable-hoc'
import AlertComponent from '../ui-components/AlertComponent'

interface OptionalConfig<T extends HomepageElement> extends HomepageElementInterface {
  type: T['type']
  title: T['title']
  config: Array<never>
}

type PartialHomepageElement = HomepageElement |
  OptionalConfig<FeaturedTopicsElement> |
  OptionalConfig<CategoryAndContentStreamHomepageElement> |
  OptionalConfig<HeroBannerHomepageElement> |
  OptionalConfig<KnowledgeBaseHomepageElement>

interface HomepageResponse {
  homepage_elements: Array<PartialHomepageElement>
  hero_image: string
  headline: string
  quick_links_border_radius: number
  enable_quick_links_border: boolean
  tabs: Array<Omit<Tab, 'phrase'>>
}

interface Props {
  knowledgeBaseHomepageTitle: string
  tabsCategories: string
  tabsRecentActive: string
  tabsUnanswered: string
}

interface State extends StateTrackerState {
  homepage_elements: Array<HomepageElement>
  isLoading: boolean
  messageType: null | 'success' | 'error'
}

let uniqueId = 0
export const getId = () => (uniqueId++)

export default class HomepageWrapper extends StateTracker<Props, State, 'homepage_elements'> {
  initialState: {homepage_elements: Array<HomepageElement>} = {
    homepage_elements: []
  }

  constructor () {
    super()
    this.state = {
      homepage_elements: [],
      hasChanged: false,
      isLoading: false,
      messageType: null
    }
    this.resolveData = this.resolveData.bind(this)
  }

  render () {
    return <div>
      { this.state.messageType ? <AlertComponent messageType={this.state.messageType} sticky isVisibleFor={3000}/> : null }
      <Homepage
        items={this.state.homepage_elements}
        onAdd={(el: HomepageElement) => {
          const els = this.state.homepage_elements
          const elements = [...els.slice(0, els.length - 1), el, ...els.slice(els.length - 1)]
          this.trackStateChange({ homepage_elements: elements })
        }}
        onRemove={(idx: number) => {
          this.trackStateChange({
            homepage_elements: this.state.homepage_elements.filter((_, index) => {
              return idx !== index
            })
          })
        }}
        onToggle={(idx: number) => {
          this.trackStateChange({
            homepage_elements: this.state.homepage_elements.map((el, index) => {
              if (idx !== index) return el
              return {...el, active: !el.active}
            })
          })
        }}
        onChange={(idx: number, data: Pick<HomepageElement, 'type' | 'config'>) => {
          this.trackStateChange({
            homepage_elements: this.state.homepage_elements.map((el, index) => {
              if (idx !== index) return el
              return Object.assign({}, el, data)
            })
          })
        }}
        onReorder={(oldIndex: number, newIndex: number) => {
          this.trackStateChange({
            homepage_elements: arrayMove(this.state.homepage_elements, oldIndex, newIndex)
          })
        }}
      />
      {this.state.hasChanged && <DynamicSaveBar
        isLoading={this.state.isLoading}
        cancelCallback={() => {
          this.unRegisterAlert()
          window.location.reload()
        }}
        saveCallback={async () => this.save()}
        buttonsPosition='Left'
      />}
    </div>
  }

  resolveData (response: HomepageResponse) {
    function hasConfig (item: PartialHomepageElement): item is HomepageElement {
      return !(item.config instanceof Array) || item.type in GenericHomepageElementTypes
    }
    return (item: PartialHomepageElement): HomepageElement => {
      item.id = getId()
      if (hasConfig(item)) return item
      switch (item.type) {
        case 'featured_topics':
        return {
          ...item,
          config: {
            topics: [],
            firstTopicsNumber: 0,
            title: '',
            buttonLabel: '',
            buttonURL: ''
          }
        }

        case 'category_and_topics_stream':
          return {
            ...item,
            config: {
              tabs: response.tabs.map((tab) => ({
                ...tab,
                phrase: ({
                  categories: this.props.tabsCategories,
                  recent_active: this.props.tabsRecentActive,
                  unanswered: this.props.tabsUnanswered
                })[tab.phrase_suffix]
              }))
            }
          }

        case 'hero_banner':
          return {
            ...item,
            config: {
              heroImage: response.hero_image,
              headline: response.headline
            }
          }

        case 'knowledge_base':
          return {
            ...item,
            config: {
              borderRadius: 4,
              enableBorder: false,
              itemsPerContainer: 100,
              isVisibleDesktop: true,
              isKnowledgeBase: true,
            }
          }
      }
    }
  }

  private getConfig<E extends HomepageElement, T extends E['type']> (type: T): Extract<E, {type: T}>['config'] {
    for (const el of this.state.homepage_elements) {
      if (el.type === type) {
        return el.config
      }
    }
    return {}
  }

  private getExtraData (): {
      hero_image: string
      headline: string
      enable_quick_links: boolean
      quick_links_border_radius: number
      enable_quick_links_border: boolean
      quick_links: Array<QuicklinkType>
      content_curation: {
        topics: Array<number>
        enabled: boolean
      }
      tabs: Array<Tab>
      } {
    const {heroImage, headline} = this.getConfig('hero_banner')
    const quickLinkConfig = this.getConfig('quick_links')
    const {curatedTopics} = this.getConfig('recommendations')
    const {tabs} = this.getConfig('category_and_topics_stream')

    return {
      hero_image: heroImage,
      headline: headline,
      enable_quick_links: true,
      quick_links_border_radius: quickLinkConfig.borderRadius,
      enable_quick_links_border: quickLinkConfig.enableBorder,
      quick_links: quickLinkConfig.quickLinks.map(link => ({
        id: link.id,
        title: link.title,
        description: link.description,
        openNewTab: link.openNewTab,
        url: link.url,
        iconSrc: link.iconSrc,
        imageSrc: link.imageSrc
      })),
      content_curation: {
        topics: curatedTopics,
        enabled: true
      },
      tabs: tabs
    }
  }

  async save () {

    this.setState({isLoading: true, messageType: null})

    try {
      await Axios.post('/homepage/homepageAjax', {
          ...this.getExtraData(),
          homepage_elements: this.state.homepage_elements
        })
    } catch (err) {
      this.setState({
        isLoading: false,
        messageType: 'error'
      })
      throw err
    }

    this.sendOrderChangeEvent()
    this.sendChangeEvents()
    this.sendAddDeleteEvents()

    this.setInitialState(this.state)
    this.setState({ isLoading: false, messageType: 'success' })
  }

  private sendAddDeleteEvents () {
    const currentItems = this.currentState ? this.currentState.homepage_elements : []
    const currentIds = new Set(currentItems.map(w => w.id))
    const initialIds = new Set(this.initialState.homepage_elements.map(w => w.id))

    this.initialState.homepage_elements.forEach(w => {
      if (!currentIds.has(w.id)) {
        tracker.trackEvent('userevent', {
          widget: w.type,
          type: 'Homepage Widget Deleted'
        })
      }
    })

    currentItems.forEach(w => {
      if (!initialIds.has(w.id)) {
        tracker.trackEvent('userevent', {
          widget: w.type,
          type: 'Homepage Widget Added'
        })
      }
    })
  }

  private sendChangeEvents () {
    const currentItems = this.currentState ? this.currentState.homepage_elements : []

    this.initialState.homepage_elements.forEach(function (item) {
      currentItems.some(function (currentItem) {
        if (currentItem.id !== item.id) return false
        if (currentItem.active !== item.active) {
          tracker.trackEvent('userevent', {
            widget: currentItem.type,
            widget_status: currentItem.active ? 'enabled' : 'disabled',
            type: 'Homepage Widget Visibility Toggled'
          })
        }
        if (JSON.stringify(currentItem.config) !== JSON.stringify(item.config)) {
          tracker.trackEvent('userevent', {
            widget: currentItem.type,
            type: 'Homepage Widget Updated'
          })
        }
        return true
      })
    })
  }

  private sendOrderChangeEvent () {
    const currentItems = this.currentState ? this.currentState.homepage_elements : []

    this.initialState.homepage_elements.some((item, index) => {
      if (!currentItems[index]) return false
      if (currentItems[index].id === item.id) return false

      tracker.trackEvent('userevent', {
        widget_order: this.state.homepage_elements.map(i => i.type),
        type: 'Homepage Widget Reordered'
      })
      return true
    })
  }

  async componentDidMount () {
    this.registerAlert()
    const res: AxiosResponse<HomepageResponse> = await Axios.get('/homepage/homepageAjax')
    const homepage_elements: Array<HomepageElement> = res.data.homepage_elements.map(this.resolveData(res.data))

    this.setInitialState({ homepage_elements })
    this.setState({ homepage_elements })
  }
}
